/* eslint-disable no-console */
import { UserInputDto } from '__generated__/globalTypes'
import {
  confirmResetPassword as amplifyConfirmResetPassword,
  confirmSignUp as amplifyConfirmSignUp,
  resetPassword as amplifyResetPassword,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  autoSignIn,
  confirmUserAttribute,
  fetchAuthSession,
  getCurrentUser,
  resendSignUpCode,
  signInWithRedirect,
  updatePassword,
  updateUserAttributes,
} from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import { createUserSession, createUserSessionVariables } from 'graphql/mutation/__generated__/createUserSession'
import { updateUser, updateUserVariables } from 'graphql/mutation/__generated__/updateUser'
import { CREATE_USER_SESSION } from 'graphql/mutation/createUserSession'
import { UPDATE_USER } from 'graphql/mutation/updateUser'
import {
  getSeriesFollowedByUser,
  getSeriesFollowedByUserVariables,
} from 'graphql/query/__generated__/getSeriesFollowedByUser'
import { getUser, getUser_getUser } from 'graphql/query/__generated__/getUser'
import {
  getUsersSession,
  getUsersSessionVariables,
  getUsersSession_getUsersSession,
} from 'graphql/query/__generated__/getUsersSession'
import { GET_SERIES_FOLLOWED_BY_USER } from 'graphql/query/getSeriesFollowedByUser'
import { GET_USER } from 'graphql/query/getUser'
import { GET_USERS_SESSION } from 'graphql/query/getUsersSession'
import { sendSomethingWrongNotification } from 'helpers/sendSomethingWrongNotification'
import { ReactNode, createContext, useContext, useEffect, useState } from 'react'
import LocalStorage from 'utils/localStorage'

import { useLazyQuery, useMutation } from '@apollo/client'

export type SocialProvider = 'Apple' | 'Google'

/**
 * The pages branch on `error.code` (e.g. 'UserNotConfirmedException'), which is what Amplify v5 set.
 * v6 puts the same Cognito exception name in `error.name` instead, so copy it across.
 */
function withCode(error: any) {
  if (error && typeof error === 'object' && !error.code && error.name) {
    error.code = error.name
  }
  return error
}

/** The origins in a comma-separated URL list, skipping blank or malformed entries. */
function originsOf(urls: string | undefined) {
  return (urls || '').split(',').flatMap((url) => {
    try {
      return url.trim() ? [new URL(url.trim()).origin] : []
    } catch {
      return []
    }
  })
}

function authError(code: string, message: string) {
  return Object.assign(new Error(message), { code, name: code })
}

type User = getUser_getUser & {
  isSeriesFollowed: boolean
  usersSessionData: getUsersSession_getUsersSession
  provider?: string
}

type Context = {
  user: User | null | undefined
  isLoading: boolean
  actions: {
    updateUserInfo: (data: UserInputDto) => Promise<void>
    signOut: () => Promise<void>
    checkAuth: () => Promise<void>
    signIn: (username: string, password: string) => Promise<void>
    signInWith: (provider: SocialProvider) => Promise<void>
    signUp: (email: string, password: string) => Promise<boolean>
    confirmSignUp: (email: string, code: string) => Promise<void>
    resendSignUp: (email: string) => Promise<void>
    changeEmail: (newEmail: string) => Promise<void>
    confirmChangeEmail: (code: string) => Promise<void>
    changePassword: (oldPassword: string, newPassword: string) => Promise<void>
    resetPassword: (username: string) => Promise<void>
    confirmResetPassword: (username: string, code: string, password: string) => Promise<void>
  }
}

const AuthContext = createContext<Context>({
  user: undefined,
  isLoading: true,
  actions: {
    updateUserInfo: async () => void 0,
    checkAuth: async () => void 0,
    signIn: async () => void 0,
    signInWith: async () => void 0,
    signUp: async () => true,
    confirmSignUp: async () => void 0,
    resendSignUp: async () => void 0,
    signOut: async () => void 0,
    changeEmail: async () => void 0,
    confirmChangeEmail: async () => void 0,
    changePassword: async () => void 0,
    resetPassword: async () => void 0,
    confirmResetPassword: async () => void 0,
  },
})

export const AuthProvider = (props: { children?: ReactNode }) => {
  const [state, setState] = useState(useContext(AuthContext))

  const [getUser] = useLazyQuery<getUser>(GET_USER, {
    fetchPolicy: 'network-only',
    context: { headers: { 'x-platform': 'web' } },
  })
  const [getUsersSession] = useLazyQuery<getUsersSession, getUsersSessionVariables>(GET_USERS_SESSION, {
    fetchPolicy: 'network-only',
  })
  const [createUserSessionMutation] = useMutation<createUserSession, createUserSessionVariables>(CREATE_USER_SESSION)
  const [getFollowedSeries] = useLazyQuery<getSeriesFollowedByUser, getSeriesFollowedByUserVariables>(
    GET_SERIES_FOLLOWED_BY_USER,
    { fetchPolicy: 'network-only', variables: { take: 1, page: 1 } }
  )
  const [updateUser] = useMutation<updateUser, updateUserVariables>(UPDATE_USER)

  async function updateUserInfo(input: UserInputDto) {
    try {
      const { data } = await updateUser({ variables: { createUserData: input } })

      setState({
        ...state,
        user: { ...state.user, ...data?.updateUser },
      })
    } catch (error) {
      console.error('[Auth]', error)
      throw error
    }
  }

  async function setUser(signedIn: boolean) {
    if (signedIn) {
      const { tokens } = await fetchAuthSession()
      if (!tokens?.accessToken) {
        throw authError('UserUnAuthenticatedException', 'User is not authenticated.')
      }
      LocalStorage.token = tokens.accessToken.toString()

      try {
        const { data } = await getUser()
        const { data: followedSeriesData } = await getFollowedSeries()

        const { data: usersSessionData } = await getUsersSession({
          variables: { sessionParams: { deviceDetails: null, sessionKey: null } },
        })

        let usersSession = usersSessionData.getUsersSession
        if (!usersSessionData.getUsersSession) {
          const { data: createdUserSessionData } = await createUserSessionMutation({
            variables: {
              sessionParams: { deviceDetails: null, sessionKey: null },
              sessionData: { playerSpeed: 1 },
            },
          })
          usersSession = createdUserSessionData.createUserSession
        }

        if (!data?.getUser) {
          throw new Error("Couldn't find user.")
        }

        // Federated (Google/Apple) users carry an `identities` claim; email/password users do not.
        const identities = tokens.idToken?.payload?.identities as { providerName?: string }[] | undefined
        const provider = identities?.[0]?.providerName
        setState({
          ...state,
          isLoading: false,
          user: {
            ...data?.getUser,
            isSeriesFollowed: !!followedSeriesData?.getSeriesFollowedByUser?.items?.length,
            usersSessionData: usersSession,
            provider,
          },
        })
      } catch (e) {
        sendSomethingWrongNotification()
        setState({
          ...state,
          isLoading: false,
        })
        await signOut()
      }
    } else {
      LocalStorage.token = null
      setState({
        ...state,
        isLoading: false,
        user: null,
      })
    }
  }

  async function signOut(): Promise<void> {
    try {
      await amplifySignOut()
    } catch (error) {
      console.error('[Auth]', error)
    } finally {
      // Clear the app's own session even if Amplify's sign-out threw (e.g. offline), so the user
      // is never left looking signed in after pressing Sign out.
      await setUser(false)
    }
  }

  async function signIn(username: string, password: string): Promise<void> {
    try {
      const input = { username: username.toLowerCase(), password }
      let result
      try {
        result = await amplifySignIn(input)
      } catch (error) {
        // v5 let a new sign-in replace a leftover session; v6 refuses. Clear it and try once more.
        if (withCode(error).code !== 'UserAlreadyAuthenticatedException') throw error
        await signOut()
        result = await amplifySignIn(input)
      }

      // v5 threw for these two; v6 returns them as a next step. Re-throw so the Login page still
      // routes unconfirmed users to "verify email" and shows the reset-password message.
      const step = result.nextStep.signInStep
      if (step === 'CONFIRM_SIGN_UP') {
        throw authError('UserNotConfirmedException', 'User is not confirmed.')
      }
      if (step === 'RESET_PASSWORD') {
        throw authError('PasswordResetRequiredException', 'Password reset required for the user.')
      }
      if (step !== 'DONE') {
        // MFA / new-password challenges: not configured for this pool, and v5 could not complete
        // them either (it ended in a failed profile load and a sign-out).
        await signOut()
        throw authError('UnsupportedSignInStep', `Unsupported sign-in step: ${step}`)
      }

      await setUser(true)
    } catch (error) {
      console.error('[Auth]', error)
      throw withCode(error)
    }
  }

  async function signInWith(provider: SocialProvider): Promise<void> {
    // Cognito only returns users to configured redirect origins, and v6 refuses to start the flow from
    // any other origin (v5 started it anyway and landed the user on the redirect origin). If the site is
    // ever opened at an address that isn't configured, send the user to the first one so they can sign in,
    // instead of showing v6's raw origin error.
    const redirectOrigins = originsOf(process.env.REACT_APP_AWS_OAUTH_REDIRECT_SIGN_IN)
    if (redirectOrigins.length && !redirectOrigins.includes(window.location.origin)) {
      const { pathname, search, hash } = window.location
      window.location.assign(redirectOrigins[0] + pathname + search + hash)
      // Never resolve: the page is unloading, and resolving would let the caller stop the button's
      // spinner and accept a second click before the navigation completes.
      return new Promise<void>(() => undefined)
    }
    try {
      await signInWithRedirect({ provider })
    } catch (error) {
      console.error('[Auth]', error)
      throw withCode(error)
    }
  }

  async function signUp(email: string, password: string): Promise<boolean> {
    const lowerCasedEmail = email.toLowerCase()
    try {
      const { isSignUpComplete } = await amplifySignUp({
        username: lowerCasedEmail,
        password,
        options: {
          userAttributes: {
            email: lowerCasedEmail,
          },
          autoSignIn: true,
        },
      })
      return isSignUpComplete
    } catch (error) {
      console.error('[Auth]', error)
      throw withCode(error)
    }
  }

  async function confirmSignUp(email: string, code: string): Promise<void> {
    try {
      const { nextStep } = await amplifyConfirmSignUp({ username: email.toLowerCase(), confirmationCode: code })
      setState({
        ...state,
        isLoading: true,
      })
      // v5 finished the automatic sign-in itself and announced it on the Hub ('autoSignIn'); v6 hands
      // it back to us as a step to complete.
      if (nextStep.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
        try {
          const { nextStep: signInStep } = await autoSignIn()
          if (signInStep.signInStep === 'DONE') {
            await setUser(true)
            return
          }
        } catch (error) {
          console.error('[Auth] Auto sign-in failed:', error)
        }
      }
      // The account is confirmed but not signed in (auto sign-in failed, or was unavailable because
      // the page was reloaded between sign-up and confirming). Stop loading — the Auth pages show only
      // a spinner while loading — and tell the user, via the Verify Email page's error line, to sign in.
      setState((prev) => ({ ...prev, isLoading: false }))
      throw authError('AutoSignInUnavailable', 'Your email is confirmed. Please sign in.')
    } catch (error) {
      console.error('[Auth]', error)
      throw withCode(error)
    }
  }

  async function resendSignUp(email: string): Promise<void> {
    try {
      await resendSignUpCode({ username: email.toLowerCase() })
    } catch (error) {
      console.error('[Auth]', error)
      throw withCode(error)
    }
  }

  async function changeEmail(newEmail: string): Promise<void> {
    try {
      await updateUserAttributes({ userAttributes: { email: newEmail } })
      // v5 announced this on the Hub ('updateUserAttributes') and the listener re-ran checkAuth.
      await checkAuth()
    } catch (error) {
      console.error('[Auth]', error)
      throw withCode(error)
    }
  }

  async function confirmChangeEmail(code: string): Promise<void> {
    try {
      await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: code })
    } catch (error) {
      console.error('[Auth]', error)
      throw withCode(error)
    }
  }

  async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
    try {
      await updatePassword({ oldPassword, newPassword })
    } catch (error) {
      console.error('[Auth]', error)
      throw withCode(error)
    }
  }

  async function resetPassword(username: string): Promise<void> {
    try {
      await amplifyResetPassword({ username: username.toLowerCase() })
    } catch (error) {
      console.error('[Auth]', error)
      throw withCode(error)
    }
  }

  async function confirmResetPassword(username: string, code: string, password: string): Promise<void> {
    try {
      await amplifyConfirmResetPassword({
        username: username.toLowerCase(),
        confirmationCode: code,
        newPassword: password,
      })
    } catch (error) {
      console.error('[Auth]', error)
      throw withCode(error)
    }
  }

  async function checkAuth(): Promise<void> {
    try {
      // Throws when nobody is signed in, like v5's currentAuthenticatedUser().
      await getCurrentUser()

      await setUser(true)
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
      }))
      console.error('[Auth]', error)
    }
  }

  useEffect(() => {
    // v5's 'autoSignIn' and 'updateUserAttributes' events no longer exist; confirmSignUp and
    // changeEmail now do that work directly. 'signedIn' is deliberately not handled: signIn() already
    // calls setUser, and loading the user twice would race to create two listening sessions.
    const stopListening = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          try {
            await setUser(true)
          } catch (error) {
            console.error('[Auth]', error)
            setState((prev) => ({ ...prev, isLoading: false }))
          }
          break
        case 'signedOut':
          await setUser(false)
          break
        case 'signInWithRedirect_failure':
          console.error('[Auth] Sign in failure:', payload.data)
          break
      }
    })

    checkAuth()

    return stopListening
  }, [])

  return (
    <AuthContext.Provider
      value={{
        ...state,
        actions: {
          updateUserInfo,
          checkAuth,
          signIn,
          signInWith,
          signUp,
          confirmSignUp,
          resendSignUp,
          signOut,
          changeEmail,
          confirmChangeEmail,
          changePassword,
          resetPassword,
          confirmResetPassword,
        },
      }}
      {...props}
    />
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)

  return {
    user: ctx.user,
    isLoading: ctx.isLoading,
    ...ctx.actions,
  }
}
